import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled, { keyframes, css } from "styled-components";
import { useForm } from "react-hook-form";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { getJob, submitApplication } from "../../lib/api";
import { getAttribution } from "../../lib/attribution";
import { getEmployer } from "../../data/companies";
import { T } from "../../theme";

/* ─── animations ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const slideIn = keyframes`
  from { opacity: 0; transform: translateX(18px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* ════════ LAYOUT ════════ */
const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${T.bg};
`;

const Wrap = styled.main`
  flex: 1;
  max-width: 680px;
  margin: 0 auto;
  padding: clamp(28px, 5vw, 52px) clamp(16px, 4vw, 32px) 96px;
  width: 100%;
  animation: ${fadeUp} 0.4s ease both;
`;

/* ════════ PROGRESS BAR ════════ */
const ProgressTrack = styled.div`
  height: 3px;
  background: ${T.border};
  border-radius: 99px;
  margin-bottom: 36px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #7c3aed, #9B51FF, #c084fc);
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${(p) => p.$pct}%;
`;

/* ════════ STEPPER ════════ */
const Stepper = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 32px;
`;

const StepPill = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1.5px solid ${(p) => p.$active ? "var(--purple)" : p.$done ? "rgba(155,81,255,.35)" : T.border};
  background: ${(p) => p.$active ? "var(--purple-soft)" : "transparent"};
  transition: all .2s;

  @media (max-width: 500px) {
    padding: 5px 8px;
  }
`;

const StepNum = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  flex-shrink: 0;
  background: ${(p) => p.$done ? "var(--purple)" : p.$active ? "var(--purple)" : T.card2};
  color: ${(p) => (p.$done || p.$active) ? "#fff" : T.muted2};
  transition: all .2s;
`;

const StepName = styled.span`
  font-size: 0.72rem;
  font-weight: ${(p) => p.$active ? "700" : "500"};
  color: ${(p) => p.$active ? "var(--purple)" : p.$done ? T.muted : T.muted2};
  white-space: nowrap;
  transition: color .2s;

  @media (max-width: 480px) { display: none; }
`;

const StepConnector = styled.div`
  flex: 1;
  height: 1.5px;
  background: ${(p) => p.$done ? "rgba(155,81,255,.4)" : T.border};
  align-self: center;
  min-width: 8px;
  transition: background .3s;
`;

/* ════════ PAGE HEADER ════════ */
const Back = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  color: ${T.muted};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 20px;
  padding: 0;
  transition: color .12s;
  &:hover { color: ${T.text}; }
`;

const PageHeader = styled.div`
  margin-bottom: 28px;
`;

const JobContext = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(155,81,255,.1);
  border: 1px solid rgba(155,81,255,.22);
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--purple);
  margin-bottom: 10px;
`;

const PageTitle = styled.h1`
  font-size: clamp(1.6rem, 3.5vw, 2.1rem);
  font-weight: 900;
  letter-spacing: -0.03em;
  color: ${T.text};
`;

/* ════════ CARD ════════ */
const Card = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 20px;
  padding: clamp(22px, 4vw, 36px);
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.28s ease both;

  /* top accent gradient */
  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c3aed, #9B51FF, #c084fc);
  }
`;

const CardTitle = styled.h2`
  font-size: 1rem;
  font-weight: 800;
  color: ${T.text};
  margin-bottom: 24px;
`;

/* ════════ FORM FIELDS ════════ */
const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols === 2 ? "1fr 1fr" : "1fr"};
  gap: 16px;
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  ${(p) => p.$span && "grid-column: 1 / -1;"}
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${T.muted};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Req = styled.span`
  color: var(--purple);
  font-size: 0.7rem;
`;

const inputShared = css`
  background: ${T.bg};
  border-radius: 10px;
  padding: 11px 14px;
  color: ${T.text};
  font-size: 0.88rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  transition: border-color .15s, box-shadow .15s;

  &::placeholder { color: ${T.muted2}; }
  &:focus {
    border-color: var(--purple);
    box-shadow: 0 0 0 3px var(--purple-soft);
  }
`;

const Input = styled.input`
  ${inputShared}
  border: 1px solid ${(p) => p.$err ? "#ef4444" : T.border};
  height: 44px;
`;

const Textarea = styled.textarea`
  ${inputShared}
  border: 1px solid ${(p) => p.$err ? "#ef4444" : T.border};
  resize: vertical;
  min-height: 120px;
  line-height: 1.6;
`;

const ErrMsg = styled.span`
  font-size: 0.73rem;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;

  &::before { content: "↑"; font-size: 0.65rem; }
`;

/* ── File upload ── */
const FileWrap = styled.div`
  position: relative;
`;

const FileDropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${T.bg};
  border: 2px dashed ${(p) => p.$has ? "var(--purple)" : p.$err ? "#ef4444" : T.borderMd};
  border-radius: 12px;
  padding: 28px 20px;
  cursor: pointer;
  text-align: center;
  transition: border-color .15s, background .15s;

  &:hover {
    border-color: var(--purple);
    background: var(--purple-soft);
  }
  &:focus-within {
    border-color: var(--purple);
    box-shadow: 0 0 0 3px var(--purple-soft);
  }
`;

const FileIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${(p) => p.$has ? "var(--purple-soft)" : T.card2};
  border: 1px solid ${(p) => p.$has ? "rgba(155,81,255,.3)" : T.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all .15s;
`;

const FileText = styled.p`
  font-size: 0.84rem;
  font-weight: ${(p) => p.$has ? "700" : "500"};
  color: ${(p) => p.$has ? "var(--purple)" : T.muted};
`;

const FileHint = styled.p`
  font-size: 0.72rem;
  color: ${T.muted2};
`;

/* ════════ QUIZ ════════ */
const QuizList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const QuizCard = styled.div`
  border: 1.5px solid ${(p) => p.$answered ? "rgba(155,81,255,.3)" : T.border};
  border-radius: 14px;
  padding: 18px 18px 14px;
  background: ${(p) => p.$answered ? "rgba(155,81,255,.04)" : T.bg};
  transition: border-color .2s, background .2s;
`;

const QuizQ = styled.p`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${T.text};
  margin-bottom: 12px;
  line-height: 1.4;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.84rem;
  color: ${(p) => p.$selected ? T.text : T.muted};
  cursor: pointer;
  padding: 9px 12px;
  border-radius: 9px;
  border: 1.5px solid ${(p) => p.$selected ? "var(--purple)" : "transparent"};
  background: ${(p) => p.$selected ? "var(--purple-soft)" : "transparent"};
  transition: all .12s;

  &:hover {
    background: var(--purple-soft);
    color: ${T.text};
    border-color: rgba(155,81,255,.35);
  }

  input { accent-color: var(--purple); flex-shrink: 0; width: 15px; height: 15px; }
`;

const ValueTag = styled.span`
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--purple);
  background: var(--purple-soft);
  border: 1px solid rgba(155, 81, 255, 0.25);
  padding: 2px 8px;
  border-radius: 999px;
  margin-bottom: 9px;
`;

const QuizIntro = styled.p`
  font-size: 0.84rem;
  color: ${T.muted};
  line-height: 1.6;
  margin-bottom: 18px;
`;

/* ════════ RESULTADO DO FIT ════════ */
const FitResult = styled.div`
  margin-top: 18px;
  padding: 18px 20px;
  border-radius: 14px;
  border: 1.5px solid ${(p) => p.$c}55;
  background: linear-gradient(120deg, ${(p) => p.$c}1f, ${(p) => p.$c}08 75%);
`;

const FitTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
`;

const FitLabel = styled.span`
  font-size: 0.95rem;
  font-weight: 800;
  color: ${T.text};
`;

const FitScore = styled.span`
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: ${(p) => p.$c};
`;

const FitBar = styled.div`
  height: 9px;
  border-radius: 999px;
  background: ${T.card2};
  overflow: hidden;
`;

const FitFill = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  border-radius: 999px;
  background: ${(p) => p.$c};
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
`;

const FitNote = styled.p`
  font-size: 0.78rem;
  color: ${T.muted};
  line-height: 1.55;
  margin-top: 11px;
`;

/* ════════ CONSENTIMENTO LGPD ════════ */
const ConsentRow = styled.label`
  display: flex;
  gap: 11px;
  align-items: flex-start;
  margin-top: 20px;
  padding: 14px 16px;
  border: 1.5px solid ${(p) => (p.$on ? "rgba(155,81,255,.4)" : T.border)};
  border-radius: 12px;
  background: ${(p) => (p.$on ? "var(--purple-soft)" : T.bg)};
  cursor: pointer;
  font-size: 0.82rem;
  color: ${T.muted};
  line-height: 1.55;
  transition: border-color 0.15s, background 0.15s;

  input { accent-color: var(--purple); width: 17px; height: 17px; margin-top: 1px; flex-shrink: 0; }
  a { color: var(--purple); font-weight: 600; }
`;

/* ════════ NAV ════════ */
const NavRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  gap: 12px;
`;

const BtnBack = styled.button`
  background: transparent;
  border: 1.5px solid ${T.border};
  color: ${T.muted};
  font-size: 0.88rem;
  font-weight: 600;
  padding: 11px 20px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  transition: border-color .14s, color .14s;
  &:hover { border-color: var(--purple); color: ${T.text}; }
`;

const BtnNext = styled.button`
  background: var(--purple);
  border: none;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 700;
  padding: 12px 28px;
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 16px rgba(155,81,255,.28);
  transition: filter .14s, transform .12s, box-shadow .14s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 6px 22px rgba(155,81,255,.35); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; box-shadow: none; }
`;

const SrOnly = styled.span`
  position: absolute; width: 1px; height: 1px; padding: 0;
  margin: -1px; overflow: hidden; clip: rect(0,0,0,0);
  white-space: nowrap; border: 0;
`;

/* ─── static data ─── */
const STEPS = ["Dados", "Links & CV", "Motivação", "Fit cultural"];

/**
 * Quiz de fit cultural — cada pergunta mapeia um valor real da Guebly
 * (ver VALUES em data/careers.js) e cada opção carrega um peso `fit`:
 *   2 = totalmente alinhado · 1 = parcial · 0 = desalinhado.
 * A pontuação final (0–100%) e o rótulo são calculados em computeFit().
 */
const FIT_QUIZ = [
  {
    id: "execucao",
    value: "Execução real",
    question: "Você teve uma ideia boa, mas ainda crua. O que faz primeiro?",
    options: [
      { label: "Coloco uma versão simples no ar rápido e itero com feedback real", fit: 2 },
      { label: "Monto um protótipo e valido com o time antes de construir", fit: 1 },
      { label: "Espero ter tudo bem definido e aprovado antes de começar", fit: 0 },
    ],
  },
  {
    id: "ownership",
    value: "Dono do problema",
    question: "Sua entrega travou por um problema que não era 'a sua parte'. Você...",
    options: [
      { label: "Assumo o resultado e corro atrás de destravar, mesmo fora do meu escopo", fit: 2 },
      { label: "Aviso, ajudo no que dá, mas espero o responsável resolver", fit: 1 },
      { label: "Fiz a minha parte; o resto não é comigo", fit: 0 },
    ],
  },
  {
    id: "transparencia",
    value: "Transparência",
    question: "Você cometeu um erro que ninguém percebeu ainda. O que faz?",
    options: [
      { label: "Conto na hora, com contexto e um plano de correção", fit: 2 },
      { label: "Corrijo quieto e comento só se perguntarem", fit: 1 },
      { label: "Se ninguém viu, não preciso levantar a mão", fit: 0 },
    ],
  },
  {
    id: "autonomia",
    value: "Autonomia com clareza",
    question: "Como você rende melhor no dia a dia?",
    options: [
      { label: "Com metas claras e liberdade total pra chegar lá do meu jeito", fit: 2 },
      { label: "Com objetivos definidos e acompanhamento próximo", fit: 1 },
      { label: "Com tarefas detalhadas e alguém ditando o passo a passo", fit: 0 },
    ],
  },
  {
    id: "remoto",
    value: "Remoto de verdade",
    question: "Trabalho 100% remoto e assíncrono, cobrando resultado. Pra você isso é...",
    options: [
      { label: "Ideal — me organizo sozinho e rendo sem cobrança constante", fit: 2 },
      { label: "Bom, mas gosto de algumas calls e ritmo combinado", fit: 1 },
      { label: "Difícil — rendo mais com presença e supervisão", fit: 0 },
    ],
  },
  {
    id: "origem",
    value: "Origem como força",
    question: "O que mais te atrai na Guebly?",
    options: [
      { label: "Construir algo de impacto, provando que talento nasce de qualquer lugar", fit: 2 },
      { label: "Crescer rápido numa holding em expansão", fit: 1 },
      { label: "Estabilidade e um cargo bem definido", fit: 0 },
    ],
  },
];

/** Calcula o fit (0–100%) e o rótulo a partir das respostas (índice por pergunta). */
function computeFit(answers) {
  const max = FIT_QUIZ.length * 2;
  const got = FIT_QUIZ.reduce((sum, q) => {
    const idx = answers[q.id];
    return sum + (idx != null ? q.options[idx].fit : 0);
  }, 0);
  const score = Math.round((got / max) * 100);
  const label = score >= 80 ? "Alto fit cultural" : score >= 50 ? "Fit moderado" : "Fit em construção";
  return { score, label };
}

/* ══════════════════════════════════
   COMPONENT
══════════════════════════════════ */
export default function Candidatar() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob]               = useState(null);
  const [step, setStep]             = useState(0);
  const [file, setFile]             = useState(null);
  const [fileErr, setFileErr]       = useState("");
  const [quiz, setQuiz]             = useState({});
  const [consent, setConsent]       = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customAnswers, setCustomAnswers] = useState({});

  const { register, handleSubmit, trigger, getValues, formState: { errors } } = useForm({ mode: "onTouched" });

  useEffect(() => {
    getJob(slug)
      .then((data) => { if (!data) navigate("/"); else setJob(data); })
      .catch(() => navigate("/"));
  }, [slug, navigate]);

  useEffect(() => {
    if (job) document.title = `Candidatura — ${job.title} · Guebly`;
    return () => { document.title = "Guebly Carreiras — Vagas abertas"; };
  }, [job]);

  const handleFile = (e) => {
    const f = e.target.files[0] || null;
    if (f && f.size > 10 * 1024 * 1024) {
      setFileErr("O arquivo excede 10 MB. Escolha um PDF menor.");
      setFile(null);
    } else {
      setFileErr("");
      setFile(f);
    }
  };

  const STEP_FIELDS = [
    ["name", "email", "phone"],
    ["linkedin"],
    ["about"],
    [],
  ];

  const customQuestions = Array.isArray(job?.processo_seletivo?.__questions) ? job.processo_seletivo.__questions : [];
  const qKey = (q, i) => q.id || `q${i}`;
  const setAnswer = (key, val) => setCustomAnswers((a) => ({ ...a, [key]: val }));

  const nextStep = async () => {
    const valid = await trigger(STEP_FIELDS[step]);
    if (!valid) return;
    // valida perguntas de triagem obrigatórias (estão na etapa "Motivação")
    if (step === 2 && customQuestions.length) {
      const missing = customQuestions.some((q, i) => q.required && !String(customAnswers[qKey(q, i)] || "").trim());
      if (missing) {
        alert("Responda as perguntas obrigatórias da vaga antes de continuar.");
        return;
      }
    }
    setStep((s) => s + 1);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const v = getValues();
      const fd = new FormData();
      fd.append("name", v.name);
      fd.append("email", v.email);
      fd.append("phone", v.phone || "");
      fd.append("linkedin", v.linkedin || "");
      fd.append("portfolio", v.portfolio || "");
      fd.append("availability", v.availability || "");
      fd.append("about", v.about || "");
      fd.append("jobId", job?.id || "");
      fd.append("jobTitle", job?.title || "");
      fd.append("source", getAttribution());

      // Respostas legíveis (valor → texto escolhido) + fit calculado.
      const quizPayload = FIT_QUIZ.reduce((acc, q) => {
        const idx = quiz[q.id];
        if (idx != null) acc[q.value] = q.options[idx].label;
        return acc;
      }, {});
      quizPayload.__fit = fit;
      fd.append("quiz", JSON.stringify(quizPayload));
      fd.append("fitScore", String(fit.score));
      fd.append("fitLabel", fit.label);
      fd.append("lgpdConsent", String(consent));

      // Respostas das perguntas de triagem (label → resposta, legível pro admin)
      const customPayload = customQuestions.reduce((acc, q, i) => {
        const val = customAnswers[qKey(q, i)];
        if (val != null && String(val).trim() !== "") acc[q.label] = val;
        return acc;
      }, {});
      fd.append("customAnswers", JSON.stringify(customPayload));

      if (file) fd.append("curriculum", file);
      const result = await submitApplication(fd);
      if (result && result.ok === false) {
        alert(result.message || "Não foi possível enviar a candidatura. Tente novamente.");
        return;
      }
      navigate("/obrigado");
    } catch {
      alert("Erro ao enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const progressPct = Math.round(((step) / STEPS.length) * 100);

  const answeredAll = FIT_QUIZ.every((q) => quiz[q.id] != null);
  const fit = computeFit(quiz);
  const fitColor = fit.score >= 80 ? "#22c55e" : fit.score >= 50 ? "#9b51ff" : "#f59e0b";

  return (
    <Page>
      <Header />
      <Wrap id="main-content">
        <Back onClick={() => step === 0 ? navigate(`/vagas/${slug}`) : setStep((s) => s - 1)}>
          <span aria-hidden="true">←</span> {step === 0 ? "Voltar para a vaga" : "Etapa anterior"}
        </Back>

        <PageHeader>
          {job && (
            <JobContext>
              <span aria-hidden="true">◎</span>
              {job.title} · {getEmployer(job.empresa).razao}
            </JobContext>
          )}
          <PageTitle>Candidatura</PageTitle>
        </PageHeader>

        <ProgressTrack aria-hidden="true">
          <ProgressFill $pct={progressPct} />
        </ProgressTrack>

        <SrOnly aria-live="polite" aria-atomic="true" role="status">
          {`Etapa ${step + 1} de ${STEPS.length}: ${STEPS[step]}`}
        </SrOnly>

        <Stepper role="list" aria-label="Etapas da candidatura">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <StepPill $active={i === step} $done={i < step} role="listitem" aria-current={i === step ? "step" : undefined}>
                <StepNum $active={i === step} $done={i < step} aria-hidden="true">
                  {i < step ? "✓" : i + 1}
                </StepNum>
                <StepName $active={i === step} $done={i < step}>{label}</StepName>
              </StepPill>
              {i < STEPS.length - 1 && <StepConnector $done={i < step} aria-hidden="true" />}
            </React.Fragment>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit(onSubmit)}>

          {/* ── STEP 0: Dados ── */}
          {step === 0 && (
            <Card>
              <CardTitle>Dados pessoais</CardTitle>
              <Grid $cols={2}>
                <Field>
                  <Label htmlFor="f-name">Nome completo <Req aria-hidden="true">*</Req></Label>
                  <Input id="f-name" $err={!!errors.name} placeholder="Seu nome completo"
                    aria-required="true" aria-describedby={errors.name ? "err-name" : undefined}
                    {...register("name", { required: "Obrigatório" })} />
                  {errors.name && <ErrMsg id="err-name" role="alert">{errors.name.message}</ErrMsg>}
                </Field>
                <Field>
                  <Label htmlFor="f-email">E-mail <Req aria-hidden="true">*</Req></Label>
                  <Input id="f-email" $err={!!errors.email} type="email" placeholder="seu@email.com"
                    aria-required="true" aria-describedby={errors.email ? "err-email" : undefined}
                    {...register("email", { required: "Obrigatório" })} />
                  {errors.email && <ErrMsg id="err-email" role="alert">{errors.email.message}</ErrMsg>}
                </Field>
                <Field>
                  <Label htmlFor="f-phone">Telefone / WhatsApp</Label>
                  <Input id="f-phone" placeholder="(11) 99999-9999" {...register("phone")} />
                </Field>
                <Field>
                  <Label htmlFor="f-avail">Disponibilidade para início</Label>
                  <Input id="f-avail" placeholder="Imediata · 30 dias · 60 dias" {...register("availability")} />
                </Field>
              </Grid>
            </Card>
          )}

          {/* ── STEP 1: Links & CV ── */}
          {step === 1 && (
            <Card>
              <CardTitle>Links &amp; Currículo</CardTitle>
              <Grid>
                <Field>
                  <Label htmlFor="f-linkedin">LinkedIn <Req aria-hidden="true">*</Req></Label>
                  <Input id="f-linkedin" $err={!!errors.linkedin} placeholder="linkedin.com/in/seuperfil"
                    aria-required="true" aria-describedby={errors.linkedin ? "err-linkedin" : undefined}
                    {...register("linkedin", { required: "LinkedIn é obrigatório" })} />
                  {errors.linkedin && <ErrMsg id="err-linkedin" role="alert">{errors.linkedin.message}</ErrMsg>}
                </Field>
                <Field>
                  <Label htmlFor="f-portfolio">Portfólio / GitHub / Site pessoal</Label>
                  <Input id="f-portfolio" placeholder="github.com/seuperfil" {...register("portfolio")} />
                </Field>
                <Field>
                  <Label htmlFor="f-cv">Currículo em PDF <span style={{ color: T.muted2, fontWeight: 400 }}>(máx. 10 MB)</span></Label>
                  <FileWrap>
                    <FileDropZone htmlFor="f-cv" $has={!!file} $err={!!fileErr}>
                      <input id="f-cv" type="file" accept=".pdf"
                        style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}
                        onChange={handleFile} aria-describedby={fileErr ? "err-cv" : undefined} />
                      <FileIcon $has={!!file} aria-hidden="true">
                        {file ? "✓" : "📄"}
                      </FileIcon>
                      <FileText $has={!!file}>
                        {file ? file.name : "Clique para selecionar o PDF"}
                      </FileText>
                      {!file && <FileHint>PDF · máx. 10 MB</FileHint>}
                    </FileDropZone>
                  </FileWrap>
                  {fileErr && <ErrMsg id="err-cv" role="alert">{fileErr}</ErrMsg>}
                </Field>
              </Grid>
            </Card>
          )}

          {/* ── STEP 2: Motivação ── */}
          {step === 2 && (
            <Card>
              <CardTitle>Motivação</CardTitle>
              <Grid>
                <Field>
                  <Label htmlFor="f-about">
                    Por que você quer trabalhar na Guebly? <Req aria-hidden="true">*</Req>
                  </Label>
                  <Textarea id="f-about" $err={!!errors.about}
                    aria-required="true" aria-describedby={errors.about ? "err-about" : undefined}
                    placeholder="Conte sobre sua motivação, o que te atrai nessa oportunidade e como você pode contribuir…"
                    {...register("about", {
                      required: "Obrigatório",
                      minLength: { value: 20, message: "Escreva um pouco mais (mínimo 20 caracteres)." },
                    })} />
                  {errors.about && <ErrMsg id="err-about" role="alert">{errors.about.message}</ErrMsg>}
                </Field>

                {customQuestions.map((q, i) => {
                  const key = qKey(q, i);
                  const val = customAnswers[key] || "";
                  const fid = `cq-${key}`;
                  return (
                    <Field key={key}>
                      <Label htmlFor={fid}>
                        {q.label} {q.required && <Req aria-hidden="true">*</Req>}
                      </Label>
                      {q.type === "long" ? (
                        <Textarea id={fid} value={val} aria-required={q.required || undefined}
                          onChange={(e) => setAnswer(key, e.target.value)} />
                      ) : q.type === "boolean" ? (
                        <RadioGroup role="radiogroup" aria-label={q.label}>
                          {["Sim", "Não"].map((opt) => (
                            <RadioLabel key={opt} $selected={val === opt}>
                              <input type="radio" name={fid} checked={val === opt}
                                onChange={() => setAnswer(key, opt)} />
                              {opt}
                            </RadioLabel>
                          ))}
                        </RadioGroup>
                      ) : q.type === "choice" && Array.isArray(q.options) ? (
                        <RadioGroup role="radiogroup" aria-label={q.label}>
                          {q.options.map((opt) => (
                            <RadioLabel key={opt} $selected={val === opt}>
                              <input type="radio" name={fid} checked={val === opt}
                                onChange={() => setAnswer(key, opt)} />
                              {opt}
                            </RadioLabel>
                          ))}
                        </RadioGroup>
                      ) : (
                        <Input id={fid} value={val} aria-required={q.required || undefined}
                          onChange={(e) => setAnswer(key, e.target.value)} />
                      )}
                    </Field>
                  );
                })}
              </Grid>
            </Card>
          )}

          {/* ── STEP 3: Fit cultural ── */}
          {step === 3 && (
            <Card>
              <CardTitle>Fit cultural — como você joga o jogo</CardTitle>
              <QuizIntro>
                Não tem resposta "certa" pra impressionar: responda com sinceridade.
                A Guebly cobra alto e dá autonomia de verdade — esse quiz mede o quanto
                o seu jeito de trabalhar combina com a nossa cultura.
              </QuizIntro>
              <QuizList>
                {FIT_QUIZ.map((q) => {
                  const answered = quiz[q.id] != null;
                  return (
                    <QuizCard key={q.id} $answered={answered}>
                      <ValueTag>{q.value}</ValueTag>
                      <QuizQ id={`quiz-label-${q.id}`}>{q.question}</QuizQ>
                      <RadioGroup role="radiogroup" aria-labelledby={`quiz-label-${q.id}`}>
                        {q.options.map((opt, oi) => (
                          <RadioLabel key={oi} $selected={quiz[q.id] === oi}>
                            <input
                              type="radio"
                              name={q.id}
                              value={oi}
                              checked={quiz[q.id] === oi}
                              onChange={() => setQuiz((prev) => ({ ...prev, [q.id]: oi }))}
                            />
                            {opt.label}
                          </RadioLabel>
                        ))}
                      </RadioGroup>
                    </QuizCard>
                  );
                })}
              </QuizList>

              {answeredAll && (
                <FitResult $c={fitColor} role="status" aria-live="polite">
                  <FitTop>
                    <FitLabel>{fit.label}</FitLabel>
                    <FitScore $c={fitColor}>{fit.score}%</FitScore>
                  </FitTop>
                  <FitBar><FitFill $pct={fit.score} $c={fitColor} /></FitBar>
                  <FitNote>
                    {fit.score >= 80
                      ? "Seu jeito de trabalhar tem tudo a ver com a Guebly. 🚀"
                      : fit.score >= 50
                      ? "Tem bastante aderência — e cultura também se constrói no dia a dia."
                      : "Talvez nosso ritmo seja bem diferente do seu — mas siga, a gente avalia a pessoa inteira."}
                    {" "}Esse resultado é só um sinal pro time; não elimina ninguém.
                  </FitNote>
                </FitResult>
              )}

              <ConsentRow $on={consent}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  aria-describedby="lgpd-text"
                />
                <span id="lgpd-text">
                  Autorizo a empresa contratante (Guebly LTDA / Guebly Contábil LTDA,
                  conforme a vaga) a tratar os meus dados pessoais (nome, contato e
                  currículo) para fins de recrutamento e seleção, conforme a{" "}
                  <a href="https://www.guebly.com.br/politica-de-privacidade" target="_blank" rel="noreferrer">
                    Política de Privacidade
                  </a>.
                </span>
              </ConsentRow>
            </Card>
          )}

          <NavRow>
            {step > 0
              ? <BtnBack type="button" onClick={() => setStep((s) => s - 1)}>← Anterior</BtnBack>
              : <span />
            }
            {step < STEPS.length - 1
              ? <BtnNext type="button" onClick={nextStep}>Próximo →</BtnNext>
              : <BtnNext type="submit" disabled={submitting || !consent} title={!consent ? "Aceite o uso dos dados (LGPD) para enviar" : undefined}>
                  {submitting ? "Enviando…" : "Enviar candidatura →"}
                </BtnNext>
            }
          </NavRow>
        </form>
      </Wrap>
      <Footer />
    </Page>
  );
}
